import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { formatTimestampTime } from '../../formatTimestamp';
import { mdiFileDocumentOutline, mdiAccountOutline, mdiCheckCircle, mdiClockOutline, mdiAlertCircle, mdiDownload } from '@mdi/js';
import { Icon } from '@mdi/react';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import './DocuSignInfo.scss';

interface DocuSignDocument {
  documentId: string;
  name: string;
  type: string;
  pages: number;
  order: number;
}

interface DocuSignRecipient {
  recipientId: string;
  recipientType: string;
  name: string;
  email: string;
  status: string;
  signedDateTime: string | null;
  deliveredDateTime: string | null;
  sentDateTime: string | null;
  routingOrder: string;
  roleName: string;
  declinedReason: string | null;
}

interface DocuSignEnvelope {
  envelopeId: string;
  status: string;
  statusDateTime: string;
  subject: string;
  emailSubject: string;
  createdDateTime: string;
  sentDateTime: string | null;
  completedDateTime: string | null;
  documents: DocuSignDocument[];
  recipients: DocuSignRecipient[];
}

interface DocuSignInfoProps {
  formId: number;
}

const DocuSignInfo: React.FC<DocuSignInfoProps> = ({ formId }) => {
  const { accessToken } = useAuth();
  const [envelopeInfo, setEnvelopeInfo] = useState<DocuSignEnvelope | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocuSignInfo = async () => {
      if (!formId) {
        setEnvelopeInfo(null);
        setError(null);
        return;
      }
      
      setLoading(true);
      setError(null);
      setEnvelopeInfo(null); // Clear previous envelope data
      
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVICE_BASEPATH}/forms/${formId}/docusign`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );
        
        setEnvelopeInfo(response.data);
      } catch (err: any) {
        if (err.response?.status === 400) {
          setError('This form is not associated with DocuSign');
        } else if (err.response?.status === 404) {
          setError('Form not found');
        } else if (err.response?.status === 500) {
          setError('DocuSign service is currently unavailable');
        } else {
          setError('Failed to load DocuSign information');
        }
        console.error('Error fetching DocuSign info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuSignInfo();
  }, [formId, accessToken]);

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Icon path={mdiCheckCircle} size={1} className="status-icon completed" />;
      case 'sent':
      case 'delivered':
        return <Icon path={mdiClockOutline} size={1} className="status-icon pending" />;
      case 'declined':
      case 'voided':
        return <Icon path={mdiAlertCircle} size={1} className="status-icon declined" />;
      default:
        return <Icon path={mdiClockOutline} size={1} className="status-icon pending" />;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'completed';
      case 'sent':
      case 'delivered':
        return 'sent';
      case 'declined':
        return 'declined';
      case 'voided':
        return 'voided';
      default:
        return 'pending';
    }
  };

  const formatDateTime = (dateTime: string | null) => {
    if (!dateTime) return 'N/A';
    try {
      return formatTimestampTime(dateTime);
    } catch {
      return dateTime;
    }
  };

  const handleDocumentDownload = (documentId: string, documentName: string) => {
    if (!envelopeInfo) return;
    
    const downloadUrl = `${process.env.REACT_APP_SERVICE_BASEPATH}/forms/${envelopeInfo.envelopeId}/documents/${documentId}/download`;
    
    // Create a temporary link to trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `${documentName}.pdf`);
    link.style.display = 'none';
    
    // Add authorization header by using fetch and creating blob URL
    fetch(downloadUrl, {
      headers: { Authorization: `Bearer ${accessToken}` }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    })
    .catch(error => {
      console.error('Error downloading document:', error);
    });
  };

  if (loading) {
    return (
      <div>
        <h3>DocuSign Information</h3>
        <div className="detail-section align-center">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h3>DocuSign Information</h3>
        <div className="detail-section">
          <div className="item">
            <dt>Status</dt>
            <dd className="error-message">{error}</dd>
          </div>
        </div>
      </div>
    );
  }

  if (!envelopeInfo) {
    return null;
  }

  return (
    <>
      <h3>DocuSign Information</h3>
      
      {/* Envelope Status */}
      <dl>
        <div className="item envelope-status-item">
          <dt>Status</dt>
          <dd>
            <div className="status-display">
              {getStatusIcon(envelopeInfo.status)}
              <span className={`status-text tag-${getStatusClass(envelopeInfo.status)}`}>
                {envelopeInfo.status.toUpperCase()}
              </span>
            </div>
          </dd>
        </div>
        <div className="item">
          <dt>Subject</dt>
          <dd>{envelopeInfo.subject}</dd>
        </div>
        <div className="item">
          <dt>Envelope ID</dt>
          <dd><code className="envelope-id">{envelopeInfo.envelopeId}</code></dd>
        </div>
        <div className="item">
          <dt>Created</dt>
          <dd>{formatDateTime(envelopeInfo.createdDateTime)}</dd>
        </div>
        {envelopeInfo.sentDateTime && (
          <div className="item">
            <dt>Sent</dt>
            <dd>{formatDateTime(envelopeInfo.sentDateTime)}</dd>
          </div>
        )}
        {envelopeInfo.completedDateTime && (
          <div className="item">
            <dt>Completed</dt>
            <dd>{formatDateTime(envelopeInfo.completedDateTime)}</dd>
          </div>
        )}
      </dl>

      {/* Documents */}
      {envelopeInfo.documents && envelopeInfo.documents.length > 0 && (
        <>
          <h3><Icon path={mdiFileDocumentOutline} size={0.8} /> Documents</h3>
          <div className="documents-section detail-section">
            <ul>
              {envelopeInfo.documents.map((doc) => (
                <li key={doc.documentId}>
                  <div className="document-list-item">
                    <div className="document-info">
                      <span className="document-name">{doc.name}</span>
                      <span className="document-details">
                        {doc.pages} page{doc.pages !== 1 ? 's' : ''} • {doc.type}
                      </span>
                    </div>
                    <button 
                      className="icon-button document-download-btn"
                      onClick={() => handleDocumentDownload(doc.documentId, doc.name)}
                      title={`Download ${doc.name}`}
                    >
                      <Icon path={mdiDownload} size={0.7} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Recipients */}
      {envelopeInfo.recipients && envelopeInfo.recipients.length > 0 && (
        <>
          <h3><Icon path={mdiAccountOutline} size={0.8} /> Recipients</h3>
          <div className="recipients-section detail-section">
            <ul>
              {envelopeInfo.recipients
                .sort((a, b) => parseInt(a.routingOrder) - parseInt(b.routingOrder))
                .map((recipient) => (
                  <li key={recipient.recipientId}>
                    <div className="recipient-list-item">
                      <div className="recipient-header">
                        {getStatusIcon(recipient.status)}
                        <div className="recipient-info">
                          <span className="recipient-name">{recipient.name}</span>
                          <span className="recipient-email">{recipient.email}</span>
                        </div>
                        <span className={`recipient-status tag-${getStatusClass(recipient.status)}`}>
                          {recipient.status}
                        </span>
                      </div>
                      <div className="recipient-details">
                        <span className="recipient-type">
                          {recipient.roleName || recipient.recipientType} (Order: {recipient.routingOrder})
                        </span>
                        {recipient.signedDateTime && (
                          <span className="signed-date">
                            Signed: {formatDateTime(recipient.signedDateTime)}
                          </span>
                        )}
                        {recipient.declinedReason && (
                          <span className="declined-reason">
                            Declined: {recipient.declinedReason}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        </>
      )}
    </>
  );
};

export default DocuSignInfo;